import type { WhatsappIntegration } from '@chukua/common';
import { Alert, Button, Popconfirm, Tag } from 'antd';
import { Plus } from 'lucide-react';
import { Link } from 'react-router';
import { RowListSkeleton } from '../../../../../shared/components/RowListSkeleton';
import { IntegrationLogo } from '../../../components/IntegrationLogo';
import { addIntegrationPath, INTEGRATION_KIND_LABELS } from '../../../integrations.paths';
import { installableProviders, sendingState } from '../../../policies/messaging-provider.policy';
import type { useWhatsappIntegrations } from '../hooks/use-whatsapp-integrations';
import { whatsappIntegrationFacts } from '../policies/whatsapp-facts.policy';
import { whatsappIntegrationPath } from '../whatsapp.paths';

type Whatsapp = ReturnType<typeof useWhatsappIntegrations>;

/** The installed WhatsApp providers, which one sends, and switching between them. */
export function WhatsappSection({ whatsapp }: { whatsapp: Whatsapp }) {
  return (
    <section aria-labelledby="whatsapp-integrations" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 id="whatsapp-integrations" className="sr-only">
          {INTEGRATION_KIND_LABELS.whatsapp}
        </h2>
        <span className="text-sm text-muted">Notifications go by WhatsApp first, and by SMS when WhatsApp can't deliver. One provider sends at a time.</span>
      </div>
      <WhatsappSectionBody whatsapp={whatsapp} />
    </section>
  );
}

function WhatsappSectionBody({ whatsapp }: { whatsapp: Whatsapp }) {
  const { state } = whatsapp;
  if (state.status === 'loading') return <RowListSkeleton label="Loading WhatsApp providers" rows={1} />;
  if (state.status !== 'loaded') return <Alert type="error" showIcon title="Couldn't load the WhatsApp providers. Try reloading the page." />;

  const sending = sendingState(state.integrations);
  const canAdd = installableProviders(state.providers, state.integrations).length > 0;
  return (
    <>
      {sending.kind === 'none-sending' && (
        <Alert type="info" showIcon title="No WhatsApp provider is sending" description="Notifications go by SMS until you turn one on below." />
      )}
      {state.integrations.length > 0 && (
        <ul className="divide-y divide-line rounded-xl border border-line bg-surface">
          {state.integrations.map((integration) => (
            <WhatsappIntegrationRow
              key={integration.id}
              integration={integration}
              sending={sending.kind === 'sending' ? sending.integration : undefined}
              whatsapp={whatsapp}
            />
          ))}
        </ul>
      )}
      {canAdd && (
        <Link
          to={addIntegrationPath('whatsapp')}
          className="flex items-center gap-2 rounded-xl border border-dashed border-line px-4 py-3 font-medium text-link hover:border-primary"
        >
          <Plus className="size-4" aria-hidden />
          {state.integrations.length > 0 ? 'Add another WhatsApp provider' : 'Add a WhatsApp provider'}
        </Link>
      )}
    </>
  );
}

interface WhatsappIntegrationRowProps {
  integration: WhatsappIntegration;
  /** The installation sending now, if any, named when switching away from it. */
  sending: WhatsappIntegration | undefined;
  whatsapp: Whatsapp;
}

function WhatsappIntegrationRow({ integration, sending, whatsapp }: WhatsappIntegrationRowProps) {
  const provider = whatsapp.state.status === 'loaded' ? whatsapp.state.providers.find((candidate) => candidate.key === integration.providerKey) : undefined;
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3.5">
      {provider && <IntegrationLogo logoSvg={provider.logoSvg} name={provider.displayName} />}
      <div className="min-w-0 flex-1 basis-56">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={whatsappIntegrationPath(integration.id)} className="font-semibold text-fg hover:text-link">
            {integration.displayName}
          </Link>
          {integration.isSending ? <Tag color="success">Sending</Tag> : <Tag>Off</Tag>}
        </div>
        <p className="flex flex-wrap gap-x-3 text-sm text-muted">
          {whatsappIntegrationFacts(integration).map((fact) => (
            <span key={fact}>{fact}</span>
          ))}
        </p>
      </div>
      <div className="flex gap-2">
        {!integration.isSending && (
          <Popconfirm
            title={sending ? `Switch WhatsApp to ${integration.displayName}?` : `Turn on ${integration.displayName}?`}
            description={
              sending
                ? `WhatsApp messages will go out through ${integration.displayName} instead of ${sending.displayName}.`
                : `WhatsApp messages will go out through ${integration.displayName}.`
            }
            okText={sending ? 'Switch' : 'Turn on'}
            onConfirm={() => whatsapp.startSending(integration)}
          >
            <Button type="primary" size="small">
              {sending ? 'Switch to this' : 'Turn on'}
            </Button>
          </Popconfirm>
        )}
        <Link to={whatsappIntegrationPath(integration.id)}>
          <Button size="small">Settings</Button>
        </Link>
      </div>
    </li>
  );
}
